// ---------------------------------------------------------------------------
// src/modules/lead/pages/LeadList.js
// ---------------------------------------------------------------------------
import { Card, Button, Dropdown, Badge } from "react-bootstrap"
import { Link, useNavigate } from "react-router-dom"
import {
  Icon,
  CustomDropdownToggle,
  CustomDropdownMenu,
  LinkList,
} from "../../../components"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import DataTable from "../../../components/DataTable/DataTable.js"
import React, { useEffect, useRef, useState } from "react"
import leadService from "../services/leadService"
import { message } from "antd"
import LeadAssignmentForm from "./AssignLeadModal.js"
import { COLOR_STATE_MAP } from "../../../constants/stateColors.js"
import mainSpecialtyService from "../../main_specialty/services/main_specialtyService"

/* -------------------------------------------------------------------------- */
/*                              COMPONENTE MAIN                               */
/* -------------------------------------------------------------------------- */

const LeadList = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  // ---------------------------- Hooks & estados --------------------------- //
  const [leadData, setLead] = useState([])
  const [loading, setLoading] = useState(false)
  const [specialtyData, setSpecialty] = useState([])
  const [mainSpecialtyData, setMainSpecialty] = useState([])
  const [provinceData, setProvince] = useState([])

  // ← NUEVO: estados pre‑derivación
  const [preStates, setPreStates] = useState([])

  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [show, setShow] = useState(false)
  const [leadToAsigne, setLeadToAsigne] = useState(false)
  const [viewMode, setViewMode] = useState("table")

  /* ------------------------------ Modales --------------------------------- */
  const handleClose = () => setShow(false)
  const handleShow = (row) => {
    setShow(true)
    setLeadToAsigne(row)
  }

  /* --------------------------- Navegación --------------------------------- */
  const toggleViewMode = () =>
    setViewMode((prev) => (prev === "table" ? "kanban" : "table"))
  const handleCreateLead = () => navigate("/lead/new")

  /* --------------------------- Carga inicial ------------------------------ */
  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    try {
      setLoading(true)
      await Promise.all([
        fetchLeadList(),
        fetchSpecialtyList(),
        fetchProvinceList(),
        fetchMainSpecialtyList(),
        fetchPreDerivationStates(), 
      ])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }


  const fetchLeadList = async (pageNumber = 1, filters = {}, limit = 10) => {
    const response = await leadService.getLeadListPaginate(
      pageNumber,
      filters,
      limit
    )
    setLead(response.results)
    setCount(response.count)
    setPage(pageNumber)
  }

  const paginateLeadList = async (pageNumber = 1) => {
    try {
      setLoading(true)
      await fetchLeadList(pageNumber)
    } catch (error) {
      console.log("Error fetching lead list:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSpecialtyList = async () =>
    setSpecialty(await leadService.getSpecialtyList())

  const fetchMainSpecialtyList = async () =>
    setMainSpecialty(await mainSpecialtyService.getMain_specialtyList())

  const fetchProvinceList = async () =>
    setProvince(await leadService.getProvinceList())

 
  const fetchPreDerivationStates = async () => {
    try {
      const data = await leadService.getPreDerivationStates()
      setPreStates(data)
    } catch (err) {
      console.error("No pude cargar pre‑states", err)
    }
  }


  const updateLead = (leadId, lawyerId) => {
    setLead(prev =>
      prev.map(l => {
        if (l.id !== leadId) return l
        const newAssignments = [...(l.assignments || []), lawyerId]
        const total          = newAssignments.length
  
        let newStateTxt = l.before_derivation_state_detail?.state
        if (total === 1)      newStateTxt = "Derivado"
        // else if (total === 2) newStateTxt = "Segunda Derivacion"
  
        const newStateObj = preStates.find(s => s.state === newStateTxt)
  
        return {
          ...l,
          assignments: newAssignments,
          before_derivation_state:      newStateObj?.id ?? l.before_derivation_state,
          before_derivation_state_detail: newStateObj
            ? { state: newStateObj.state }
            : l.before_derivation_state_detail,
        }
      }),
    )
  }
  const handlePreStateChange = async (leadId, newStateId) => {
    try {
      await leadService.updateLead(leadId, {
        before_derivation_state: newStateId,
      })
      setLead((lds) =>
        lds.map((l) =>
          l.id === leadId
            ? {
                ...l,
                before_derivation_state: newStateId,
                before_derivation_state_detail: preStates.find(
                  (s) => s.id === +newStateId
                ),
              }
            : l
        )
      )
      message.success("Estado pre‑derivación actualizado")
    } catch (err) {
      console.error(err)
      message.error("No se pudo actualizar")
    }
  }

  const handleDeleteLead = async (leadId) => {
    try {
      setLoading(true)
      await leadService.deleteLead(leadId)
      setLead((data) => data.filter((rows) => rows.id !== leadId))
    } finally {
      setLoading(false)
    }
  }

  /* ------------------------- Export / Import Excel ------------------------ */
  const exportExcel = async (is_assigned = false) => {
    try {
      setLoading(true)
      const response = await leadService.exportLead(is_assigned)
      if (response.status === 200) {
        const blob = new Blob([response.data], {
          type: "application/vnd.ms-excel",
        })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = downloadUrl
        const cd = response.headers["content-disposition"]
        let filename = is_assigned ? "leads.xlsx" : "leads-sin-asignar.xlsx"
        if (cd) {
          const m = cd.match(/filename=\"?(.+)\"?/)
          if (m) filename = m[1]
        }
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      } else throw new Error()
    } catch (e) {
      console.error("Error export:", e)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (event) => {
    try {
      setLoading(true)
      const file = event.target.files[0]
      if (!file) return
      const formData = new FormData()
      formData.append("file", file)
      const res = await leadService.importLead(formData)
      if (res.status === 200) {
        await fetchLeadList()
        message.success("File uploaded successfully")
      } else {
        message.error("Error uploading file")
      }
    } catch (err) {
      console.error(err)
      message.error("Error uploading file")
    } finally {
      setLoading(false)
      event.target.value = ""
    }
  }

  /* ------------------------------------------------------------------------ */
  /*                                COLUMNAS                                  */
  /* ------------------------------------------------------------------------ */
  const leadColumns = [
    /* --- id y fecha ------------------------------------------------------ */
    { name: "id", selector: (row) => row.id, sortable: true },
    {
      name: "fecha",
      selector: (row) =>
        new Date(row.assignation_date).toLocaleString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      sortable: true,
    },

    /* --- Estado pre‑derivación (editable) -------------------------------- */
    {
      name: "Estado pre‑derivación",
      selector: (r) => r.before_derivation_state_detail?.state || "",
      sortable: true,
      center: true,
      headerStyle: { textAlign: "center" },
      style: { textAlign: "center" },
      cell: (r) => (
        <select
          value={r.before_derivation_state || ""}
          onChange={(e) => handlePreStateChange(r.id, e.target.value)}
          style={{ width: "100%" }}
        >
          <option value="">Sin estado</option>
          {preStates.map((s) => (
            <option key={s.id} value={s.id}>
              {s.state}
            </option>
          ))}
        </select>
      ),
    },

   
    { name: "nombre", selector: (row) => row.name, sortable: true },
    { name: "apellidos", selector: (row) => row.last_name, sortable: true },
    { name: "origen", selector: (row) => row.origin, sortable: true },
    {
      name: "beneficio esperado",
      selector: (row) =>
        row.lead_assignments?.length
          ? row.lead_assignments
              .map((a) => (a.estimated_value ? `${a.estimated_value} €` : "-"))
              .join(", ")
          : "-",
      sortable: true,
    },
    { name: "email", selector: (row) => row.email, sortable: true },
    { name: "teléfono", selector: (row) => row.phone_number, sortable: true },
    {
      name: "especialidad",
      selector: (row) => {
        const sp = specialtyData.find((s) => s.id === row.specialty)
        return sp ? sp.name : ""
      },
      sortable: true,
      cell: (row) => {
        const ms = mainSpecialtyData.find((m) => m.id === row.main_specialty)
        return ms ? ms.name : ""
      },
    },
    {
      name: "sub-especialidad",
      selector: (row) => {
        const sp = specialtyData.find((s) => s.id === row.specialty)
        return sp ? sp.child_specialty : ""
      },
      sortable: true,
    },
    {
      name: "provincia",
      selector: (row) => {
        const pr = provinceData.find((p) => p.id === row.province)
        return pr ? pr.name : ""
      },
      sortable: true,
    },
    {
      name: "sms verificado",
      selector: (row) =>
        row.sms_verified ? (
          <Badge bg="success">Yes</Badge>
        ) : (
          <Badge bg="warning">No</Badge>
        ),
      sortable: true,
    },
    {
      name: "asignaciones",
      selector: (row) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {row.assignments?.length ? (
            row.assignments.map((a) => (
              <Badge bg="info" key={a}>
                <Link to={`/lawyer/${a}`}>
                  <span style={{ color: "white" }}>{a}</span>
                </Link>
              </Badge>
            ))
          ) : (
            <Badge bg="danger">Sin asignar</Badge>
          )}
          {(!row.assignments || row.assignments.length < 3) && (
            <Button size="sm" onClick={() => handleShow(row)}>
              Asignar
            </Button>
          )}
        </div>
      ),
    },
    {
      name: "estados",
      minWidth: "500px",
      selector: (row) => row.state_assignment,
      cell: (row) =>
        row.state_assignment?.length ? (
          <span>
            {row.state_assignment.map((sa) => {
              const st = sa.state?.state || ""
              const sb = sa.substate?.substate || ""
              const bg = COLOR_STATE_MAP[st] || "#5f38f9"
              return (
                <Badge key={sa.id} style={{ backgroundColor: bg, marginRight: "5px" }}>
                  {st}
                  {sb ? ` | ${sb}` : ""}
                </Badge>
              )
            })}
          </span>
        ) : (
          <Badge style={{ backgroundColor: "#5f38f9", color: "white" }}>
            Sin estados
          </Badge>
        ),
      sortable: true,
    },
    {
      name: "acciones",
      cell: (row) => (
        <div className="text-end w-100">
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              as={CustomDropdownToggle}
              className="btn btn-sm btn-icon btn-zoom me-n1"
            >
              <Icon name="more-v" />
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="dropdown-menu-sm"
              as={CustomDropdownMenu}
              align="end"
              renderOnMount                   
              container={document.body}       
              popperConfig={{ strategy: "fixed" }}
              style={{ zIndex: 3000 }}       
            >
              <div className="dropdown-content py-1">
                <LinkList className="link-list-hover-bg-primary link-list-md">
                  <Link to={`/lead/edit/${row.id}`} className="dropdown-item">
                    <Icon name="edit" />
                    <span>Edit</span>
                  </Link>
                  <button
                    className="dropdown-item"
                    onClick={() => handleDeleteLead(row.id)}
                  >
                    <Icon name="trash" />
                    <span>Delete</span>
                  </button>
                </LinkList>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
      sortable: false,
    },
  ]

  /* ------------------------------------------------------------------------ */
  /*                                  RENDER                                  */
  /* ------------------------------------------------------------------------ */
  return (
    <Layout title="Leads" content="container" loading={loading}>
     
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Leads</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Leads
                </li>
              </ol>
            </nav>
          </Block.HeadContent>

          <Block.HeadContent>
            <ul className="d-flex gap-2">
              <li>
                <Button
                  className="d-md-none"
                  size="md"
                  variant="primary"
                  onClick={handleCreateLead}
                >
                  <Icon name="plus" />
                  <span>Añadir</span>
                </Button>
              </li>
              <li>
                <a
                  href="/templates/Plantilla-importación.xlsx"
                  download="Plantilla-importación-leads.xlsx"
                  style={{ textDecoration: "none" }}
                >
                  <Button className="d-none d-md-inline-flex" variant="primary">
                    <Icon name="file" />
                    <span>Plantilla excel</span>
                  </Button>
                </a>
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex"
                  variant="primary"
                  onClick={() => fileInputRef.current.click()}
                >
                  <Icon name="file" />
                  <span>Importar</span>
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                />
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex"
                  variant="primary"
                  onClick={() => exportExcel(true)}
                >
                  <span>Exportar</span>
                </Button>
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex"
                  variant="primary"
                  onClick={() => exportExcel(false)}
                >
                  <span>Exportar sin asignar</span>
                </Button>
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex"
                  variant="primary"
                  onClick={handleCreateLead}
                >
                  <Icon name="plus" />
                  <span>Añadir Lead</span>
                </Button>
              </li>
              <li>
                <Button
                  className="d-none d-md-inline-flex"
                  variant="secondary"
                  onClick={toggleViewMode}
                >
                  <Icon name="grid" />
                  <span>
                    {viewMode === "table" ? "Vista Kanban" : "Vista Tabla"}
                  </span>
                </Button>
              </li>
            </ul>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

     
      <Block>
        {viewMode === "table" ? (
          <Card>
            {!loading && (
              <DataTable
                tableClassName="data-table-head-light table-responsive"
                data={leadData}
                columns={leadColumns}
                expandableRows
                actions
                searchByList={["name", "last_name", "email", "phone_number"]}
                backEndPagination
                fetchPaginatedData={paginateLeadList}
                pageCount={count}
                currentPaginationPage={page}
                backendFilter
                filterFunc={fetchLeadList}
                isLeadForm
              />
            )}
          </Card>
        ) : (
          <KanbanBoard />
        )}
      </Block>

      {/* --------------------- MODAL ASIGNAR LEAD --------------------------- */}
      <LeadAssignmentForm
        show={show}
        handleClose={handleClose}
        lead={leadToAsigne}
        updateLead={updateLead}
      />
    </Layout>
  )
}

/* -------------------------------------------------------------------------- */
/*                               KANBAN BOARD                                 */
/* -------------------------------------------------------------------------- */
const KanbanBoard = () => {
 
  const [kanbanData, setKanbanData] = useState([])
  const [loadingKanban, setLoadingKanban] = useState(false)
  const [statePages, setStatePages] = useState({})
  const [searchQuery, setSearchQuery] = useState("")

  const fetchKanbanData = async () => {
    try {
      setLoadingKanban(true)
      const data = await leadService.getLeadStateFiveRecord()
      setKanbanData(data)
      const init = {}
      data.forEach((col) => (init[col.id] = 5))
      setStatePages(init)
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingKanban(false)
    }
  }

  useEffect(() => {
    if (!searchQuery) fetchKanbanData()
  }, [searchQuery])

  const handleSearch = async () => {
    try {
      setLoadingKanban(true)
      const body = { name: searchQuery }
      const res = await leadService.filterByName(body)
      setKanbanData(res.data)
    } finally {
      setLoadingKanban(false)
    }
  }

  const handleLoadMore = async (stateId) => {
    try {
      const currentPage = statePages[stateId] || 5
      const res = await leadService.getMoreLeads(stateId, currentPage)
      const { data: moreLeads, has_more_leads } = res.data
      setKanbanData((prev) =>
        prev.map((col) =>
          col.id === stateId
            ? { ...col, lead: [...col.lead, ...moreLeads], has_more_leads }
            : col
        )
      )
      setStatePages((prev) => ({ ...prev, [stateId]: currentPage + 5 }))
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div style={{ marginTop: "2rem" }}>
      <h3 className="mb-3">Kanban View</h3>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar por nombre..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleSearch}
          style={{ marginTop: "0.5rem" }}
        >
          Buscar
        </Button>
      </div>
      {loadingKanban ? (
        <p>Cargando datos Kanban...</p>
      ) : (
        <div style={{ display: "flex", gap: "1rem", overflowX: "auto" }}>
          {kanbanData.map((column) => (
            <Card
              key={column.id}
              style={{ minWidth: "250px", flexShrink: 0 }}
              className="shadow-sm"
            >
              <Card.Header className="bg-primary text-white">
                {column.state || "Sin estado"}
              </Card.Header>
              <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
                {column.lead?.length ? (
                  column.lead.map((lead) => (
                    <div
                      key={lead.id}
                      style={{
                        marginBottom: "0.5rem",
                        padding: "0.5rem",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <strong>
                        {lead.name} {lead.last_name}
                      </strong>
                      <p className="mb-0" style={{ fontSize: "0.85rem" }}>
                        {lead.email}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No hay leads</p>
                )}
              </Card.Body>
              {column.has_more_leads && (
                <Card.Footer className="text-center">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleLoadMore(column.id)}
                  >
                    Ver +
                  </Button>
                </Card.Footer>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default LeadList
